import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { Skeleton } from './Skeleton';

export interface RailTimelineProps<T> {
  headLabel: string; // "Bookings" / "Leave" / "Activity"
  items: T[];
  dateOf: (t: T) => string; // ISO date — for newest-first sort
  renderNode: (t: T) => ReactNode; // the full .node (marks the active one .on)
  // The group key + heading for each spine segment. Defaults to the calendar year
  // (bookings/leave); the edit-user activity rail passes a per-DAY heading so the
  // continuous spine bands by day instead — same component, same chrome.
  groupOf?: (t: T) => string;
  // The "+ New" head action. OMITTED for read-only timelines (e.g. activity),
  // which then render the head label alone with no new button.
  onNew?: () => void;
  newActive?: boolean;
  emptyText?: ReactNode; // shown in .tl when there are no items
  // While the timeline's items are still loading, show a skeleton instead of the
  // emptyText — so "No leave yet" can't flash before the real entries land.
  loading?: boolean;
  // Fired when the scroll nears the bottom — for lazy "load more as you scroll"
  // (the edit-user activity rail). Omitted by timelines that load all at once.
  onReachEnd?: () => void;
  // Show a loading footer under the list while the next page is fetching.
  loadingMore?: boolean;
  // The selected item's id. When it changes (or the list hydrates), the timeline
  // scrolls the active node into view — so opening a stay deep in a long history
  // doesn't leave it off-screen above the fold.
  activeKey?: string | number | null;
  // What the head counts, when that is not every item — the owner rail lists a
  // household's viewings among its stays and counts the stays alone, because a
  // viewing is never a booking (viewings-in-the-bookings-list AC2).
  count?: number;
}

// The rail timeline shared by every master-detail modal: an (optional) "+ New"
// head and a newest-first list grouped under headings (one continuous spine per
// group). Source of truth = the owner↔booking modal's timeline, promoted to a
// global — bookings/leave band by year, the edit-user activity rail bands by day.
export function RailTimeline<T>({ headLabel, items, dateOf, renderNode, groupOf, onNew, newActive, emptyText, loading, onReachEnd, loadingMore, activeKey, count }: RailTimelineProps<T>) {
  const tlRef = useRef<HTMLDivElement>(null);
  const keyOf = groupOf ?? ((t: T) => dateOf(t).slice(0, 4));

  // Centre the active node within the .tl scroll container (only if off-screen),
  // re-run when the selection changes or the list hydrates from seed → full.
  useLayoutEffect(() => {
    const c = tlRef.current;
    const el = c?.querySelector<HTMLElement>('.node.on');
    if (!c || !el) return;
    const cr = c.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    if (er.top < cr.top || er.bottom > cr.bottom) {
      c.scrollTop += er.top - cr.top - (cr.height - er.height) / 2;
    }
  }, [activeKey, items.length]);

  const sorted = [...items].sort((a, b) => dateOf(b).localeCompare(dateOf(a)));
  const groups: { key: string; items: T[] }[] = [];
  for (const t of sorted) {
    const k = keyOf(t);
    const last = groups[groups.length - 1];
    if (last && last.key === k) last.items.push(t);
    else groups.push({ key: k, items: [t] });
  }

  return (
    <>
      <div className="tlhead">
        <span className="eyebrow">{headLabel} · {count ?? items.length}</span>
        {onNew && <button type="button" className={'newbtn' + (newActive ? ' active' : '')} onClick={onNew}>+ New</button>}
      </div>
      <div
        className="tl"
        ref={tlRef}
        onScroll={onReachEnd ? (e) => {
          const el = e.currentTarget;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) onReachEnd();
        } : undefined}
      >
        {loading && items.length === 0
          ? <div className="tl-loading" aria-busy="true" role="status">{[0, 1, 2].map((i) => <Skeleton key={i} variant="row" />)}</div>
          : groups.length === 0
          ? <div className="empty" style={{ padding: '14px 16px' }}>{emptyText}</div>
          : <>
              {groups.map((g) => (
                <div key={g.key}><div className="yr">{g.key}</div><div className="yeargroup">{g.items.map(renderNode)}</div></div>
              ))}
              {loadingMore && <div className="tl-loading tl-more" aria-busy="true" role="status"><Skeleton variant="row" /></div>}
            </>}
      </div>
    </>
  );
}
